import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolFileList } from '@synergy/components';
import { PropsWithChildren, ReactElement } from 'react';
import { converter } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import Scrollspy from 'react-scrollspy';

import { Inquiry } from '../../../common/models/inquiry';
import { OfferAccountPurchase } from '../../../common/models/offer-account-purchase';
import { useAppSelector } from '../../../store/store';

const c = tokens.color;

const Entry = ({ label, children }: PropsWithChildren<{ label: ReactElement }>): ReactElement => (
    <Box sx={{ marginBottom: '15px' }}>
        <Box
            sx={{
                fontSize: '9.5px',
                fontWeight: 600,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: c.ink3,
                marginBottom: '3px',
            }}
        >
            {label}
        </Box>
        <Box sx={{ fontSize: '12.5px', color: c.ink, '& p': { margin: 0 } }}>{children}</Box>
    </Box>
);

/**
 * The content of the offer mask's dock: who to ask, what the buyer wrote, the
 * attached files and a jump list over the customers of this inquiry.
 *
 * The legacy sidebar brought its own dark title bar, which belonged to neither
 * look once the theme changed. The dock around this supplies the heading.
 */
export const PaloOfferDetailDock = ({
    inquiry,
    offers,
}: {
    inquiry: Inquiry;
    offers: OfferAccountPurchase[];
}): ReactElement => {
    const mediaBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const anchors = [...offers.map(offer => `customer-${offer.accountPurchase.id}`), 'conditions'];

    const jumpTo = (id: string): void => {
        document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <Box>
            <Entry label={<FormattedMessage id="app.label.responsibleContact" />}>
                <Box>{inquiry.responsibleContact?.fullName}</Box>
                <Box sx={{ color: c.ink2 }}>{inquiry.responsibleContact?.email}</Box>
                <Box sx={{ color: c.ink2 }}>{inquiry.responsibleContact?.phone}</Box>
            </Entry>

            {inquiry.noteToSupplier && (
                <Entry label={<FormattedMessage id="app.offers.details.noteToSupplier" />}>
                    <Box dangerouslySetInnerHTML={{ __html: converter.makeHtml(inquiry.noteToSupplier) }} />
                </Entry>
            )}

            {inquiry.media.length > 0 && (
                <Entry label={<FormattedMessage id="app.offers.details.media" />}>
                    <PoolFileList acceptedFiles={inquiry.media} downloadBaseUrl={mediaBaseUrl} hideTitle={true} />
                </Entry>
            )}

            <Box
                sx={{
                    borderTop: `1px solid ${c.line}`,
                    marginTop: '4px',
                    paddingTop: '9px',
                    '& ul': { listStyle: 'none', margin: 0, padding: 0 },
                    '& li': {
                        fontSize: '12.5px',
                        color: c.ink2,
                        padding: '6px 8px',
                        borderRadius: tokens.radius.small + 'px',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: c.surface2, color: c.ink },
                    },
                    '& li.palo-jump-active': { backgroundColor: c.accentSoft, color: c.accentHi, fontWeight: 600 },
                }}
            >
                <Scrollspy items={anchors} currentClassName="palo-jump-active" rootEl="#scroll-container">
                    {offers.map((item, index) => (
                        <li key={item.offer.id} onClick={() => jumpTo(`customer-${item.accountPurchase.id}`)}>
                            <FormattedMessage id="app.common.customer" values={{ value: index + 1 }} />
                            {item.accountPurchase.customerAccount.name}
                        </li>
                    ))}
                    <li onClick={() => jumpTo('conditions')}>
                        <FormattedMessage id="app.common.conditions" />
                    </li>
                </Scrollspy>
            </Box>
        </Box>
    );
};
